import { sanitizePdfText } from "helpers";
import { t } from "i18next";
import { RowInput, Styles } from "jspdf-autotable";

import { OrderDetails, OrderProductItem } from "../../orders.types";
import {
  GroupedOrderItem,
  groupOrderItemsByPack
} from "../groupItemsByPack";

interface getProductsProps {
  orderDetails: OrderDetails;
  normalFontStyles: Partial<Styles>;
}

const PACK_FILL_COLOR = "#F5F5F5";
const PACK_ITEM_LEFT_PADDING = 18;
const DEFAULT_HORIZONTAL_PADDING = 5;

interface CellStyleOptions {
  fillColor?: string;
  leftPadding?: number;
}

export const getProducts = ({
  orderDetails,
  normalFontStyles
}: getProductsProps) => {
  const cellStyles = (
    halign: "left" | "right",
    isLast: boolean,
    options?: CellStyleOptions
  ): Partial<Styles> => {
    const left = options?.leftPadding ?? DEFAULT_HORIZONTAL_PADDING;
    const styles: Partial<Styles> = {
      valign: "middle",
      halign,
      ...normalFontStyles,
      cellPadding: {
        top: 1,
        bottom: isLast ? 5 : 1,
        left,
        right: DEFAULT_HORIZONTAL_PADDING
      }
    };
    if (options?.fillColor) {
      styles.fillColor = options.fillColor;
    }
    return styles;
  };

  const formatItemLine = (item: OrderProductItem): string => {
    const detail = `${t("Size")}: ${item.barcode.sizeAttributeValue.value}  •  ${t("Color")}: ${t(item.barcode.productVariant.colorAttributeValue.value)}  •  ${t("Qty")}: ${item.quantity}`;
    return `${sanitizePdfText(item.barcode.productVariant.name)}\n${detail}`;
  };

  const formatStandalonePrice = (item: OrderProductItem): string => {
    const { totalPricePerItem, quantity } = item;
    return quantity === 1
      ? `${totalPricePerItem.toLocaleString("en-US")} FCFA`
      : `${totalPricePerItem.toLocaleString("en-US")} FCFA * ${quantity} = ${(totalPricePerItem * quantity).toLocaleString("en-US")} FCFA`;
  };

  const grouped: GroupedOrderItem[] = groupOrderItemsByPack(
    orderDetails?.items ?? []
  );

  const products: RowInput[] = [];

  grouped.forEach((entry, entryIndex) => {
    const isLastEntry = entryIndex === grouped.length - 1;

    if (entry.kind === "standalone") {
      products.push([
        {
          content: formatItemLine(entry.item),
          styles: cellStyles("left", isLastEntry)
        },
        {
          content: formatStandalonePrice(entry.item),
          styles: cellStyles("right", isLastEntry)
        }
      ]);
      return;
    }

    const { group } = entry;
    const packTitle = group.packTitle
      ? sanitizePdfText(group.packTitle)
      : `${t("Pack")} #${entryIndex + 1}`;

    const rowsTotal = group.rows.reduce(
      (sum, row) => sum + row.totalPricePerItem * row.quantity,
      0
    );
    const packPriceValue = group.packPrice ?? rowsTotal;
    const packPriceContent = `${packPriceValue.toLocaleString("en-US")} FCFA`;

    const hasRows = group.rows.length > 0;
    const headerIsLast = isLastEntry && !hasRows;

    products.push([
      {
        content: packTitle,
        styles: cellStyles("left", headerIsLast, { fillColor: PACK_FILL_COLOR })
      },
      {
        content: packPriceContent,
        styles: cellStyles("right", headerIsLast, { fillColor: PACK_FILL_COLOR })
      }
    ]);

    group.rows.forEach((row, rowIndex) => {
      const isLastRow = rowIndex === group.rows.length - 1;
      const cellIsLast = isLastEntry && isLastRow;
      products.push([
        {
          content: formatItemLine(row),
          styles: cellStyles("left", cellIsLast, {
            fillColor: PACK_FILL_COLOR,
            leftPadding: PACK_ITEM_LEFT_PADDING
          })
        },
        {
          content: "",
          styles: cellStyles("right", cellIsLast, {
            fillColor: PACK_FILL_COLOR
          })
        }
      ]);
    });
  });

  return { products };
};
